import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CALENDLY_URL = 'https://calendly.com/mandieozee/15min';

const cards = [
  {
    text: 'You are high-achieving, and emotionally running on empty.',
  },
  {
    text: 'You give endlessly to others and do not know how to receive.',
  },
  {
    text: 'You have done the work, and still feel like something is missing.',
  },
  {
    text: 'You are ready to stop coping and start actually healing.',
  },
];

export default function TruthSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const eyebrowRef = useRef<HTMLParagraphElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        eyebrowRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
        }
      );
      gsap.fromTo(
        headlineRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
      gsap.fromTo(
        bodyRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 65%',
          },
        }
      );
      if (cardsRef.current) {
        const cardEls = cardsRef.current.children;
        gsap.fromTo(
          cardEls,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
            },
          }
        );
      }
      gsap.fromTo(
        buttonRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: buttonRef.current,
            start: 'top 85%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-[#FDFAF8] py-16 md:py-28 lg:py-32"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-5xl mx-auto">
        <p
          ref={eyebrowRef}
          className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase text-center mb-6"
        >
          This is for you
        </p>

        <h2
          ref={headlineRef}
          className="font-heading text-2xl md:text-4xl lg:text-5xl leading-[1.2] text-[#1A1A1A] text-center mb-8"
        >
          You have built a beautiful life.
          <br />
          So why does it still feel like
          <br />
          something is missing?
        </h2>

        <p
          ref={bodyRef}
          className="font-body text-sm md:text-base text-[#4A4A4A] leading-relaxed text-center max-w-3xl mx-auto mb-14"
        >
          You are not ungrateful. You are not dramatic. You are not asking for
          too much. You are a woman who has spent years giving her best to everything
          and everyone, and quietly, in the spaces between, losing yourself. You
          are exhausted in a way that sleep cannot fix. Disconnected in a way that
          success cannot fill. And somewhere beneath all the doing and achieving and
          showing up, there is a woman who just wants to finally feel at home in her
          own skin. She has been there the whole time. This work brings her back.
        </p>

        {/* Gold Divider */}
        <div className="w-16 h-px bg-[#C9A84C] mx-auto mb-14" />

        {/* Cards Grid */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 justify-items-center mb-12 px-0 md:px-0"
        >
          {cards.map((card, index) => (
            <div
              key={index}
              className="bg-[#FFFFFF] border border-[#E8D5C8] rounded-2xl px-5 py-6 md:px-10 md:py-10 w-full max-w-[440px] relative overflow-hidden group hover:border-[#C9A84C]/40 transition-colors duration-300"
            >
              {/* Subtle gold accent bar at top */}
              <div className="absolute top-0 left-6 right-6 h-0.5 bg-[#C9A84C]/30 rounded-full" />
              <p className="font-body text-sm md:text-base text-[#1A1A1A] leading-relaxed">
                {card.text}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div ref={buttonRef} className="text-center">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap"
          >
            This is me. I&apos;m ready.
          </a>
        </div>
      </div>
    </section>
  );
}